import { isValidEmail } from "@faire/deprecated-retailer-visitor-shared/lib/validation";
import { INVITE_TOAST_AUTO_DISMISS_TIME_MS } from "@faire/deprecated-retailer-visitor-shared/views/settings/manage-users/common/const";
import { InviteUserToast } from "@faire/deprecated-retailer-visitor-shared/views/settings/manage-users/common/InviteUserToast";
import { useInviteUser } from "@faire/deprecated-retailer-visitor-shared/views/settings/manage-users/common/useInviteUser";
import { Core } from "@faire/design-tokens";
import supportArticle from "@faire/web-api--source/routes/www/support/articles/articleId";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Input } from "@faire/web/slate-deprecated/Input";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { createToast } from "@faire/web/slate/Toast/Toast";
import { Typography } from "@faire/web/slate/Typography";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { type JSX } from "react";
import styled from "styled-components";

interface IProps {
  onInviteSent?: () => void;
  onInviteButtonClick?: () => void;
}

const LinkFormatting = (chunks: JSX.Element, link: string) => (
  <EmbeddedLink linkTo={link} target="_blank" color={Core.text.subdued}>
    {chunks}
  </EmbeddedLink>
);

export const InviteTeamMemberSection: React.FC<IProps> = ({
  onInviteSent,
  onInviteButtonClick,
}) => {
  const [emailAddress, setEmailAddress] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const { strictLocalize } = useStrictLocalization();

  const learnMoreLink = supportArticle("8675579278619");
  const invalidEmailErrorMessage = strictLocalize({
    id: "I4p1eb",
    defaultMessage: "Invalid email address",
    description: { text: "Error when user has entered an invalid email" },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(e.target.value);
  };

  const { handleInviteUser } = useInviteUser();

  const validateEmail = () => {
    if (!emailAddress.trim()) {
      setErrorMessage(invalidEmailErrorMessage);
      return false;
    }

    if (!isValidEmail(emailAddress.trim())) {
      setErrorMessage(invalidEmailErrorMessage);
      return false;
    }

    return true;
  };

  const createInviteSentToast = (userDetails: {
    email: string;
    accountToken: string;
  }) => {
    createToast(
      <InviteUserToast
        email={userDetails.email}
        accountToken={userDetails.accountToken}
      />,
      {
        icon: "foundation:Send",
        timeout: INVITE_TOAST_AUTO_DISMISS_TIME_MS,
      }
    );
  };

  const handleInvite = async () => {
    onInviteButtonClick?.();
    if (!validateEmail()) {
      return;
    }

    setErrorMessage("");
    const email = emailAddress.trim();
    const { error, accountUserToken } = await handleInviteUser(email);

    if (error) {
      setErrorMessage(error);
      return;
    }

    createInviteSentToast({
      email: email,
      accountToken: accountUserToken ?? "",
    });
    onInviteSent?.();
  };

  return (
    <>
      <Row>
        <Input
          label={strictLocalize({
            id: "RAaA3l",
            defaultMessage: "Email address",
            description: {
              text: "label for input field for user to enter email address",
              img: "https://cdn.faire.com/fastly/bd9219d99c33185051191c966ed979967d3b830e2d89a94324a90c02958a223f.png",
            },
          })}
          data-test-id="invite-user-email-address-input"
          value={emailAddress}
          onChange={handleInputChange}
        />
        <Spacer width="2x" />
        <InviteButton
          variant="secondary"
          small
          onClick={handleInvite}
          data-test-id="invite-user-button"
        >
          <StrictLocalMsg
            id="dFHB2m"
            defaultMessage="Invite"
            description={{
              text: "Button text to invite a new user",
              img: "https://cdn.faire.com/fastly/bd9219d99c33185051191c966ed979967d3b830e2d89a94324a90c02958a223f.png",
            }}
          />
        </InviteButton>
      </Row>
      {errorMessage ? (
        <Typography color={Core.text.critical}>{errorMessage}</Typography>
      ) : null}
      <Spacer height="4x" />
      <Typography color={Core.text.subdued}>
        <StrictLocalMsg
          id="JA1lEz"
          defaultMessage="Account owners are ultimately responsible for team member actions on their account. <LinkFormatting>Learn more</LinkFormatting>"
          description={{
            text: "Lets user know more details about inviting a team member, with a link to a help article",
            img: "https://cdn.faire.com/fastly/bd9219d99c33185051191c966ed979967d3b830e2d89a94324a90c02958a223f.png",
          }}
          values={{
            LinkFormatting: (chunks) => LinkFormatting(chunks, learnMoreLink),
          }}
        />
      </Typography>
    </>
  );
};

const InviteButton = styled(Button)`
  align-self: end;
`;
